import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  useRevokeDomain,
  useDeleteThirdPartyDomain,
  useUpdateDomainAutoRenew,
} from '../hooks/useMyDomains'
import { useActiveWorkspace, useSelectionStore } from '@/state/selection.store'
import { hasFeature } from '@/lib/hasFeature'
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle,
} from '@/components/catalyst-ui/dialog'
import { Button } from '@/components/catalyst-ui/button'
import {
  Dropdown,
  DropdownButton,
  DropdownMenu,
  DropdownItem,
  DropdownLabel,
} from '@/components/catalyst-ui/dropdown'
import { XMarkIcon, CheckBadgeIcon } from '@heroicons/react/24/solid'
import { EllipsisVerticalIcon } from '@heroicons/react/24/solid'
import { Heading } from '@/components/catalyst-ui/heading'
import { useProjects } from '@/features/workspace/hooks/useProjects'
import { useAuth } from '@/features/auth/hooks/useAuth'
import { useWorkspaceDomainsWithNs, useListUserDomains } from '../hooks/useMyDomains'
import { usePermissions } from '@/hooks/usePermissions'
import DisconnectProjectDialog from './DisconnectProjectDialog'
import DeleteDomainDialog from './DeleteDomainDialog'
import RevokeDomainDialog from './RevokeDomainDialog'
import ConnectProjectConfirmDialog from './ConnectProjectConfirmDialog'
import DomainConnectedSuccessDialog from './DomainConnectedSuccessDialog'
import ConnectProjectDropdown from './ConnectProjectDropdown'

export default function DomainActionMenu({
  domain,
  onManage,
  onConnect,
  onDisconnect,
  onDelete,
}) {
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [showDisconnectDialog, setShowDisconnectDialog] = useState(false)
  const [showProjectSelectionDialog, setShowProjectSelectionDialog] =
    useState(false)
  const [showConnectConfirmDialog, setShowConnectConfirmDialog] = useState(false)
  const [showSuccessDialog, setShowSuccessDialog] = useState(false)
  const [selectedProject, setSelectedProject] = useState(null)
  const [connectedProject, setConnectedProject] = useState(null)
  const [showRevokeDialog, setShowRevokeDialog] = useState(false)
  const [revokeNotes, setRevokeNotes] = useState('')
  const { isOwnerOrAdmin } = usePermissions()
  const [deleteConfirmText, setDeleteConfirmText] = useState('')
  const [revokeConfirmText, setRevokeConfirmText] = useState('')
  const [deleteConfirmations, setDeleteConfirmations] = useState({
    projectConnection: false,
    dnsChecks: false,
    teamAccess: false,
    cannotUndo: false,
  })
  const navigate = useNavigate()
  const { workspaceId } = useActiveWorkspace()
  const { projects } = useProjects(workspaceId)
  const { user } = useAuth()
  const { data: wsDomains } = useWorkspaceDomainsWithNs(workspaceId)
  const { data: userDomainsData } = useListUserDomains(user?.id, { workspaceId, offset: 0, limit: 100 })
  const revokeDomainMutation = useRevokeDomain()
  const deleteThirdPartyDomainMutation = useDeleteThirdPartyDomain()
  const updateAutoRenewMutation = useUpdateDomainAutoRenew()

  const handleManage = () => {
    onManage(domain)
  }

  const handleToggleAutoRenew = () => {
    // Get current auto-renew status from domain
    const currentAutoRenew = domain.rawDomain?.auto_renew || domain.auto_renew || false
    // Toggle auto-renew
    updateAutoRenewMutation.mutate({
      domain: domain.name,
      autoRenew: !currentAutoRenew
    });
  }

  const handleConnectProject = () => {
    setShowProjectSelectionDialog(true)
  }

  // Check if a project is already connected to any domain
  const isProjectConnectedToAnyDomain = (projectId) => {
    const pid = String(projectId)

    // Check withNS domains
    const list = wsDomains?.data || []
    const withNsHit = list.some((d) => {
      const connectedPid = String(
        d.project_id ||
        d.projectId ||
        (typeof d.project === 'string' ? d.project : (d.project?._id || d.project?.id) || '')
      )
      return connectedPid && connectedPid === pid
    })

    // Also consider third-party domains connected to projects
    const thirdPartyList = userDomainsData?.data || []
    const thirdPartyHit = thirdPartyList.some((item) => {
      const connectedPid = String(
        typeof item.project === 'string' ? item.project : (item.project?._id || item.project?.id) || ''
      )
      return connectedPid && connectedPid === pid
    })

    return withNsHit || thirdPartyHit
  }

  const handleProjectSelect = project => {
    if (onConnect && project) {
      onConnect(domain, project)
    }
    setShowConnectConfirmDialog(false)
    setShowProjectSelectionDialog(false)
    setSelectedProject(null)
  }

  const handleProjectSelectFromDropdown = (project) => {
    if (project) {
      if (project?.order?.paid) {
        setSelectedProject(project)
        setShowConnectConfirmDialog(true)
      } else {
        const normalizedProject = { ...project, id: project.id || project._id }
        useSelectionStore.getState().setCurrentProject(normalizedProject)
        hasFeature('DOMAIN_CONNECTION', () => {
          setSelectedProject(project)
          setShowConnectConfirmDialog(true)
        }, ['limitPremium'])
      }
    }
  }

  const handleConfirmConnect = () => {
    if (selectedProject) {
      setConnectedProject(selectedProject)
      handleProjectSelect(selectedProject)
      setShowConnectConfirmDialog(false)
      setShowSuccessDialog(true)
    }
  }

  const handleDisconnectProject = () => {
    setShowDisconnectDialog(true)
  }

  const handleConfirmDisconnect = () => {
    if (onDisconnect) {
      onDisconnect(domain)
    }
    setShowDisconnectDialog(false)
  }

  const handleDeleteDomain = () => {
    // For withNS domains, show revoke dialog
    if (domain.domainType === 'withNS') {
      setShowRevokeDialog(true)
    } else {
      // For third-party domains, show delete dialog
      setDeleteConfirmations({
        projectConnection: false,
        dnsChecks: false,
        teamAccess: false,
        cannotUndo: false,
      })
      setDeleteConfirmText('')
      setShowDeleteDialog(true)
    }
  }

  const handleConfirmDelete = () => {
    console.log('Deleting third-party domain:', domain)
    console.log('Domain name:', domain.name)
    console.log('User ID:', user?.id)

    // Only handle third-party domain deletion
    deleteThirdPartyDomainMutation.mutate(
      {
        userId: user?.id,
        domain: domain.name,
      },
      {
        onSuccess: data => {
          console.log('Delete third-party domain success:', data)
          setShowDeleteDialog(false)
          // Call the original onDelete callback if provided
          if (onDelete) {
            onDelete(domain)
          }
        },
        onError: error => {
          console.error('Delete third-party domain error:', error)
        },
      }
    )
  }

  const handleConfirmRevoke = () => {
    console.log('Revoking domain:', domain)
    console.log('Domain name:', domain.name)
    console.log('Revoke notes:', revokeNotes)

    revokeDomainMutation.mutate(
      {
        domain: domain.name,
        notes: revokeNotes,
      },
      {
        onSuccess: data => {
          console.log('Revoke success:', data)
          setShowRevokeDialog(false)
          setRevokeNotes('')
          // Call the original onDelete callback if provided
          if (onDelete) {
            onDelete(domain)
          }
        },
        onError: error => {
          console.error('Revoke error:', error)
        },
      }
    )
  }

  // Check if domain is connected (has project_id for withNS or project for thirdParty)
  const isConnected =
    (domain.domainType === 'withNS' && domain.project_id) ||
    (domain.domainType === 'thirdParty' && domain.project) ||
    domain.connected

  // Get current auto-renew status for withNS domains
  const currentAutoRenew = domain.domainType === 'withNS'
    ? (domain.rawDomain?.auto_renew || domain.auto_renew || false)
    : false

  const menuItems = [
    {
      label: 'Manage',
      action: handleManage,    },
    // Only show auto-renew for withNS domains
    ...(domain.domainType === 'withNS' ? [{
      label: currentAutoRenew ? 'Disable Auto-Renew' : 'Enable Auto-Renew',
      action: handleToggleAutoRenew,    }] : []),
    // Show either Connect or Disconnect based on connection status
    isConnected
      ? {
        label: 'Disconnect Project',
        action: handleDisconnectProject,
      }
      : {
        label: 'Connect Project',
        action: handleConnectProject,
      },
      
    ...(isOwnerOrAdmin ? [{
      label: domain.domainType === 'withNS' ? 'Revoke Domain' : 'Delete Domain',
      action: handleDeleteDomain,      
      danger: true,
    }] : []),
  ]

  return (
    <>
      <Dropdown>
        <DropdownButton
          as={Button}
          plain
          className="p-1 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 data-focus:!ring-2 data-focus:!ring-indigo-500 data-focus:!ring-offset-2"
          title="More options"
        >
          <EllipsisVerticalIcon className="!w-4.5 !h-4.5" />
        </DropdownButton>
        <DropdownMenu anchor="bottom end">
          {menuItems.map((item, index) => (
            <DropdownItem
              key={index}
              onClick={item.action}
              className={item.danger ? '!text-[#B91C1C] hover:!text-[#B91C1C] hover:!bg-[#B91C1C]/10' : ''}
            >
              <DropdownLabel >{item.label}</DropdownLabel>
            </DropdownItem>
          ))}
        </DropdownMenu>
      </Dropdown>

      {/* Disconnect Confirmation Dialog */}
      <DisconnectProjectDialog
        open={showDisconnectDialog}
        onClose={() => setShowDisconnectDialog(false)}
        domainName={domain?.name}
        onConfirm={handleConfirmDisconnect}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteDomainDialog
        open={showDeleteDialog}
        onClose={() => {
          setShowDeleteDialog(false)
          setDeleteConfirmText('')
          setDeleteConfirmations({
            projectConnection: false,
            dnsChecks: false,
            teamAccess: false,
            cannotUndo: false,
          })
        }}
        domainName={domain?.name}
        onConfirm={handleConfirmDelete}
        isLoading={deleteThirdPartyDomainMutation.isPending}
        requireConfirmations={true}
      />

      {/* Project Selection Dialog */}
      <ConnectProjectDropdown
        projects={projects.filter(project => !isProjectConnectedToAnyDomain(project.id || project._id))}
        onSelect={handleProjectSelectFromDropdown}
        buttonText=""
        buttonClassName=""
        open={showProjectSelectionDialog}
        onOpenChange={(open) => {
          setShowProjectSelectionDialog(open)
        }}
        domainName={domain?.name}
        description={
          <>Select a project to connect <strong className="text-black font-bold">{domain?.name}</strong> to</>
        }
        skipGating={true}
      />

      {/* Connect Confirmation Dialog */}
      <ConnectProjectConfirmDialog
        open={showConnectConfirmDialog}
        onClose={() => {
          setShowConnectConfirmDialog(false)
          setSelectedProject(null)
        }}
        domainName={domain?.name}
        projectName={selectedProject?.name || 'project name'}
        onConfirm={handleConfirmConnect}
      />

      {/* Revoke Domain Dialog */}
      <RevokeDomainDialog
        open={showRevokeDialog}
        onClose={() => {
          setShowRevokeDialog(false)
          setRevokeConfirmText('')
        }}
        domainName={domain?.name}
        onConfirm={handleConfirmRevoke}
        isLoading={revokeDomainMutation.isPending}
      />


      {/* Success Dialog */}
      <DomainConnectedSuccessDialog
        open={showSuccessDialog}
        onClose={() => setShowSuccessDialog(false)}
        domainName={domain?.name}
        projectName={connectedProject?.name || 'project'}
        onGoToProject={() => {
          if (domain?.domainType === 'thirdParty') {
            // Navigate to records page for DNS re-verification
            navigate(`/domains/my-domain/records?domain=${encodeURIComponent(domain.name)}&workspace=${workspaceId || ''}`)
          } else if (connectedProject) {
            const projectId = connectedProject.id || connectedProject._id || connectedProject.uniqueUrl
            if (projectId && workspaceId) {
              navigate(`/overview?workspace=${workspaceId}&project=${projectId}`)
            }
          }
          setShowSuccessDialog(false)
        }}
      />
    </>
  )
}
